import { forwardRef, KeyboardEvent, KeyboardEventHandler, useEffect, useImperativeHandle, useState } from 'react'

import { SuggestionItem } from '~lib/editor/extensions/commands/getSuggestionItems'
import MenuLayout, { MenuVariant } from '~lib/ui-kit/MenuLayout'
import MenuList from '~lib/ui-kit/MenuList'
import Tooltip from '~lib/ui-kit/TooltipV2'

export type CommandsListProps = {
  items: SuggestionItem[]
  command: (item: SuggestionItem) => void
}

export type CommandListRefObject = {
  onKeyDown: KeyboardEventHandler<HTMLElement>
}

const CommandsList = forwardRef<CommandListRefObject, CommandsListProps>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0)

  useEffect(() => {
    setSelectedIndex(0)
  }, [items])

  const selectItem = (index: number) => {
    const item = items[index]

    if (item && !item.disabled) {
      command(item)
    }
  }

  useImperativeHandle(ref, () => ({
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((prev) => {
          return (prev + items.length - 1) % items.length
        })

        return true
      }

      if (event.key === 'ArrowDown') {
        setSelectedIndex((prev) => {
          return (prev + 1) % items.length
        })

        return true
      }

      if (event.key === 'Enter') {
        selectItem(selectedIndex)
        return true
      }

      return false
    },
  }))

  return (
    <MenuLayout variant={MenuVariant.CONTEXT}>
      <MenuList>
        {items.length > 0 ? (
          items.map((item, idx) =>
            item.hint ? (
              <Tooltip key={idx} content={item.hint} placement="right-start">
                <MenuList.Item
                  disabled={item.disabled}
                  active={selectedIndex === idx}
                  icon={item.icon}
                  onMouseEnter={() => {
                    requestAnimationFrame(() => {
                      setSelectedIndex(idx)
                    })
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    selectItem(idx)
                  }}
                >
                  {item.label}
                </MenuList.Item>
              </Tooltip>
            ) : (
              <MenuList.Item
                disabled={item.disabled}
                active={selectedIndex === idx}
                key={idx}
                icon={item.icon}
                onMouseEnter={() => {
                  requestAnimationFrame(() => {
                    setSelectedIndex(idx)
                  })
                }}
                onMouseDown={(e) => {
                  e.preventDefault()
                  selectItem(idx)
                }}
              >
                {item.label}
              </MenuList.Item>
            )
          )
        ) : (
          <MenuList.Item disabled>No result</MenuList.Item>
        )}
      </MenuList>
    </MenuLayout>
  )
})

export default CommandsList
